// Pure resume orchestration for the WEB /adyen-redirect return route (CPS-26589,
// Milestone B, Task 8, decision 7). Extracted out of the screen component so the
// load-bearing logic — restore the stashed session, ensure a valid B2C token,
// fire ADYEN_SUBMIT_PAYMENT_DETAILS with the URL's redirectResult, classify the
// result — is testable under the node jest preset WITHOUT rendering the screen
// (which needs jsdom + navigation + MSAL we don't have here).
//
// The screen wires the side-effecting pieces (the useMutation submit fn, the
// AuthManager token-ensure, reading window.location, navigate) and feeds them in
// here; this function does no React, no DOM, no navigation. AUTHORISED hands the
// cardId back so the screen can drive useAdyenCardPolling to Active; the terminal
// buckets carry the refusal category for a brief message before navigating away.

import type { AdyenPaymentResult } from 'components/payment/adyenCardAddSheet.types';
import type { AdyenRedirectSession } from 'utils/adyenRedirectSession.types';

export type AdyenRedirectOutcome =
  // No (or stale) stash, or no redirectResult on the URL: do NOT submit, do NOT
  // error. The screen just navigates to payment-methods; readCard polling there
  // surfaces the still-pending card.
  | { kind: 'noSession' }
  // Submit returned AUTHORISED (or PENDING, webhook-final): the PAN is being
  // tokenized server-side. The screen polls readCard(cardId) to Active, then
  // navigates.
  | { kind: 'authorised'; cardId: string }
  // Submit returned REFUSED/FAILED: brief categorized message, then navigate.
  | { kind: 'refused'; refusalReasonCategory?: string | null }
  // Submit threw, or returned an unmapped/empty result: brief generic message,
  // then navigate. (An `action` coming back here would be an unexpected second
  // redirect for a card-add; we treat it as non-terminal-error rather than
  // looping, and let readCard polling on the destination settle the truth.)
  | { kind: 'error' };

export type ResumeAdyenRedirectDeps = {
  // The full return URL (e.g. window.location.href). We read `redirectResult`
  // off it ourselves rather than trusting a pre-parsed value.
  url: string;
  // Reads the sessionStorage stash (null if absent/stale). On web this is
  // restoreAdyenRedirectSession; the three ids are all the submit needs.
  restore: () => AdyenRedirectSession | null;
  // Ensures a valid B2C access token (silent MSAL re-acquire if expired),
  // matching the app's existing AuthManager.getAccessTokenAsync path. Awaited
  // before the submit so the resumed mutation rides a fresh token. Failures are
  // swallowed by the caller's link layer; we await purely to trigger the refresh.
  ensureToken: () => Promise<unknown>;
  // Fires ADYEN_SUBMIT_PAYMENT_DETAILS and resolves the AdyenPaymentResult.
  submit: (input: {
    cardId: string;
    transactionId: string;
    checkoutAttemptId: string;
    details: { redirectResult: string };
  }) => Promise<AdyenPaymentResult | undefined>;
};

const getRedirectResult = (url: string): string => {
  try {
    return new URL(url).searchParams.get('redirectResult') ?? '';
  } catch {
    return '';
  }
};

export const resumeAdyenRedirect = async ({
  url,
  restore,
  ensureToken,
  submit,
}: ResumeAdyenRedirectDeps): Promise<AdyenRedirectOutcome> => {
  const redirectResult = getRedirectResult(url);
  const session = restore();

  // Either half missing -> nothing to resume. Not an error: the readCard poll on
  // the payment-methods screen will surface the pending card regardless.
  if (!redirectResult || !session) {
    return { kind: 'noSession' };
  }

  // Ensure a valid token before resuming (silent re-acquire if expired). We don't
  // block on its specific value — the Apollo auth link reads the token per
  // request — but awaiting it forces the refresh up front, matching the app path.
  try {
    await ensureToken();
  } catch {
    // A token failure surfaces as an auth error on the submit below; don't
    // pre-empt it here.
  }

  try {
    const result = await submit({
      cardId: session.cardId,
      transactionId: session.transactionId,
      checkoutAttemptId: session.checkoutAttemptId,
      details: { redirectResult },
    });

    if (!result) {
      return { kind: 'error' };
    }

    // payment-service forwards Adyen's PascalCase result codes ("Authorised",
    // "Refused", "Pending"), so normalize to uppercase before matching — exactly as
    // the in-sheet useAdyenCardAdd.applyResult path does. Matching the raw value
    // against the uppercase cases previously dropped a successful "Authorised" into
    // the default branch -> {kind:'error'}, briefly showing the generic error copy
    // and skipping the activation poll even though tokenization succeeded.
    switch (result.resultCode?.toUpperCase()) {
      // AUTHORISED is a sync success; PENDING is non-terminal (webhook-final per
      // ADR-014). Both hand off to the readCard poll, which owns settling the card
      // to Active — same as applyResult routing both to `activating`.
      case 'AUTHORISED':
      case 'PENDING':
        return { kind: 'authorised', cardId: session.cardId };
      case 'REFUSED':
      case 'FAILED':
        return {
          kind: 'refused',
          refusalReasonCategory: result.refusalReasonCategory,
        };
      default:
        // RECEIVED / an unexpected second action / any unmapped code: treat as a
        // non-terminal error here and let the destination's readCard polling settle
        // the actual state.
        return { kind: 'error' };
    }
  } catch {
    return { kind: 'error' };
  }
};
